// src/components/tasks/RruleEditor.jsx

import { useState, useRef, useEffect } from "react";
import { RRule } from "rrule";

/**
 * @typedef {'none' | 'daily' | 'weekly' | 'annually' | 'weekdays' | 'custom'} RruleRepeatType
 * @typedef {'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY'} RruleFreq
 * @typedef {'never' | 'count' | 'until'} RruleEndType
 */

/**
 * @param {object} props
 * @param {RRule | string | null | undefined} [props.rrule]
 * @param {Date | null} [props.dtstart]
 * @param {string[] | null | undefined} [props.exdate]
 * @param {(data: {rrule: RRule | null, exdate: string[]}) => void} props.onChange
 */
export const RRuleEditor = ({ rrule, dtstart, exdate, onChange }) => {
  /** @type {ReturnType<typeof useState<RruleRepeatType>>} */
  const [repeatType, setRepeatType] = useState(/** @type {RruleRepeatType} */ ("none"));

  const [interval, setInterval] = useState(1);

  /** @type {ReturnType<typeof useState<RruleFreq>>} */
  const [freq, setFreq] = useState(/** @type {RruleFreq} */ ("WEEKLY"));

  /** @type {ReturnType<typeof useState<number[]>>} */
  const [byweekday, setByweekday] = useState([]);

  /** @type {ReturnType<typeof useState<RruleEndType>>} */
  const [endsType, setEndsType] = useState(/** @type {RruleEndType} */ ("never"));

  const [count, setCount] = useState(10);
  const [until, setUntil] = useState("");

  /** @type {ReturnType<typeof useState<string[]>>} */
  const [exceptions, setExceptions] = useState(exdate || []);
  const [exceptionInput, setExceptionInput] = useState("");

  /* Track the last rrule we generated to avoid re-parsing own output */
  const lastGeneratedRrule = useRef(/** @type {string | null} */ (null));

  /* Store dtstart as timestamp to avoid Date object reference issues */
  const dtstartTime = useRef(/** @type {number | null} */ (null));

  /* NEW: Track last emitted payload to prevent onChange loops */
  const lastEmitted = useRef({ rrule: null, exdateKey: "" });
  const exdateKey = (arr) => (Array.isArray(arr) ? arr.join("|") : "");

  /* Update dtstart timestamp when prop changes */
  useEffect(() => {
    const newTime = dtstart ? dtstart.getTime() : null;
    dtstartTime.current = newTime;
  }, [dtstart]);

  /* Keep exceptions in sync when prop changes (optional but safer) */
  useEffect(() => {
    const next = Array.isArray(exdate) ? exdate : [];
    const currKey = exdateKey(exceptions);
    const nextKey = exdateKey(next);
    if (currKey !== nextKey) setExceptions(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exdate]);

  /* Initialize from existing rrule */
  useEffect(() => {
    if (!rrule) return;

    const rruleString = typeof rrule === "string" ? rrule : rrule.toString();

    /* Don't re-parse if this is the rrule we just generated */
    if (lastGeneratedRrule.current === rruleString) return;

    try {
      const options = RRule.parseString(rruleString);

      /* Determine repeat type */
      if (options.freq === RRule.DAILY && options.interval === 1) {
        setRepeatType("daily");
      } else if (
        options.freq === RRule.WEEKLY &&
        options.interval === 1 &&
        (!options.byweekday || options.byweekday.length === 0)
      ) {
        setRepeatType("weekly");
      } else if (options.freq === RRule.YEARLY && options.interval === 1) {
        setRepeatType("annually");
      } else if (options.freq === RRule.DAILY && options.byweekday && options.byweekday.length === 5) {
        setRepeatType("weekdays");
      } else {
        setRepeatType("custom");
        setInterval(options.interval || 1);
        setFreq(
          options.freq === RRule.DAILY
            ? "DAILY"
            : options.freq === RRule.WEEKLY
            ? "WEEKLY"
            : options.freq === RRule.MONTHLY
            ? "MONTHLY"
            : "YEARLY"
        );
        if (options.byweekday) {
          setByweekday(options.byweekday.map((d) => d.weekday));
        }
      }

      /* Set ends configuration */
      if (options.count) {
        setEndsType("count");
        setCount(options.count);
      } else if (options.until) {
        setEndsType("until");
        const date = new Date(options.until);
        setUntil(date.toISOString().split("T")[0]);
      } else {
        setEndsType("never");
      }
    } catch (error) {
      console.error("Error parsing rrule:", error);
    }
  }, [rrule]);

  /* Build rrule object when settings change */
  useEffect(() => {
    const nextExKey = exdateKey(exceptions);

    if (repeatType === "none") {
      // Emit only if changed
      if (lastEmitted.current.rrule === null && lastEmitted.current.exdateKey === nextExKey) return;

      lastEmitted.current = { rrule: null, exdateKey: nextExKey };
      onChange({ rrule: null, exdate: exceptions });
      return;
    }

    const options = {};

    /* Set dtstart to preserve timezone */
    if (dtstart) {
      options.dtstart = dtstart;
    }

    /* Set frequency and interval based on repeat type */
    switch (repeatType) {
      case "daily":
        options.freq = RRule.DAILY;
        options.interval = 1;
        break;
      case "weekly":
        options.freq = RRule.WEEKLY;
        options.interval = 1;
        break;
      case "annually":
        options.freq = RRule.YEARLY;
        options.interval = 1;
        break;
      case "weekdays":
        options.freq = RRule.DAILY;
        options.byweekday = [RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR];
        break;
      case "custom":
        options.freq =
          freq === "DAILY"
            ? RRule.DAILY
            : freq === "WEEKLY"
            ? RRule.WEEKLY
            : freq === "MONTHLY"
            ? RRule.MONTHLY
            : RRule.YEARLY;
        options.interval = interval;

        if (freq === "WEEKLY" && byweekday.length > 0) {
          const map = [RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR, RRule.SA, RRule.SU];
          options.byweekday = byweekday.map((day) => map[day]);
        }
        break;
      default:
        break;
    }

    /* Set end condition */
    if (endsType === "count") {
      options.count = count;
    } else if (endsType === "until") {
      if (until) options.until = new Date(until);
    }

    try {
      const built = new RRule(options);
      const rruleString = built.toString();

      /* Store the generated rrule to prevent re-parsing */
      lastGeneratedRrule.current = rruleString;

      // Emit only if changed
      if (lastEmitted.current.rrule === rruleString && lastEmitted.current.exdateKey === nextExKey) return;

      lastEmitted.current = { rrule: rruleString, exdateKey: nextExKey };
      onChange({ rrule: built, exdate: exceptions });
    } catch (error) {
      console.error("Error creating rrule:", error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repeatType, interval, freq, byweekday, endsType, count, until, exceptions]);

  const weekdayNames = ["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"];

  const toggleWeekday = (day) => {
    setByweekday((prev) => (prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]));
  };

  const addException = () => {
    if (!exceptionInput) return;

    const newDate = new Date(exceptionInput);

    const dtStartDate = new Date(dtstartTime.current);

    // Preserve time from dtstart
    newDate.setHours(dtStartDate.getHours(), dtStartDate.getMinutes(), dtStartDate.getSeconds(), 0);

    const iso = newDate.toISOString();

    const isDuplicate = exceptions.some((d) => d === iso);

    if (!isDuplicate) {
      const updated = [...exceptions, iso];
      setExceptions(updated);
      setExceptionInput("");
    }
  };

  const removeException = (dateToRemove) => {
    const updated = exceptions.filter((d) => d !== dateToRemove);
    setExceptions(updated);
  };

  return (
    <div className="rrule-editor">
      <label className="rrule-editor__repeat-label">
        <strong>Herhalen</strong>
        <select
          className="rrule-editor__select"
          value={repeatType}
          onChange={(e) => setRepeatType(/** @type {RruleRepeatType} */ (e.target.value))}
        >
          <option value="none">Niet herhalen</option>
          <option value="daily">Dagelijks</option>
          <option value="weekly">Wekelijks</option>
          <option value="annually">Jaarlijks</option>
          <option value="weekdays">Elke werkdag (ma-vr)</option>
          <option value="custom">Aangepast...</option>
        </select>
      </label>

      {repeatType === "custom" && (
        <div className="rrule-editor__custom-panel">
          <div className="rrule-editor__interval-row">
            <label>Herhaal elke</label>
            <input
              className="rrule-editor__interval-input"
              type="number"
              min="1"
              value={interval}
              onChange={(e) => setInterval(parseInt(e.target.value) || 1)}
            />
            <select
              className="rrule-editor__freq-select"
              value={freq}
              onChange={(e) => setFreq(/** @type {RruleFreq} */ (e.target.value))}
            >
              <option value="DAILY">dag(en)</option>
              <option value="WEEKLY">week/weken</option>
              <option value="MONTHLY">maand(en)</option>
              <option value="YEARLY">jaar/jaren</option>
            </select>
          </div>

          {freq === "WEEKLY" && (
            <div className="rrule-editor__weekdays">
              <label className="rrule-editor__weekdays-label">Herhaal op:</label>
              <div className="rrule-editor__weekday-buttons">
                {weekdayNames.map((name, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className={`rrule-editor__weekday-button ${
                      byweekday.includes(idx) ? "rrule-editor__weekday-button--active" : ""
                    }`}
                    onClick={() => toggleWeekday(idx)}
                  >
                    {name}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="rrule-editor__ends-section-label">Eindigt:</label>
            <div className="rrule-editor__ends-options">
              <label className="rrule-editor__ends-option">
                <input
                  type="radio"
                  name="endsType"
                  value="never"
                  checked={endsType === "never"}
                  onChange={(e) => setEndsType(/** @type {RruleEndType} */ (e.target.value))}
                />
                Nooit
              </label>

              <label className="rrule-editor__ends-option">
                <input
                  type="radio"
                  name="endsType"
                  value="count"
                  checked={endsType === "count"}
                  onChange={(e) => setEndsType(/** @type {RruleEndType} */ (e.target.value))}
                />
                Na
                <input
                  className="rrule-editor__count-input"
                  type="number"
                  min="1"
                  value={count}
                  onChange={(e) => setCount(parseInt(e.target.value) || 1)}
                  disabled={endsType !== "count"}
                />
                keer
              </label>

              <label className="rrule-editor__ends-option">
                <input
                  type="radio"
                  name="endsType"
                  value="until"
                  checked={endsType === "until"}
                  onChange={(e) => setEndsType(/** @type {RruleEndType} */ (e.target.value))}
                />
                Op
                <input
                  className="rrule-editor__date-input"
                  type="date"
                  value={until}
                  onChange={(e) => setUntil(e.target.value)}
                  disabled={endsType !== "until"}
                />
              </label>
            </div>
          </div>
        </div>
      )}

      {repeatType !== "none" && (
        <div>
          <h2>Uitzonderingen</h2>
          <p className="add-item-notice-p">
            Voeg specifieke datums toe waarop de activiteit <strong>niet</strong> doorgaat.
          </p>

          <div className="exceptions-input-row">
            <input type="date" value={exceptionInput} onChange={(e) => setExceptionInput(e.target.value)} />
            <button type="button" className="button" onClick={addException}>
              Toevoegen
            </button>
          </div>

          {exceptions.length > 0 ? (
            <div className="exceptions-chip-list">
              {exceptions.map((d, idx) => (
                <span key={`${d}-${idx}`} className="chip">
                  {new Date(d).toLocaleDateString("nl-NL", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                  <button
                    type="button"
                    aria-label={`Verwijder ${new Date(d).toLocaleDateString("nl-NL")}`}
                    onClick={() => removeException(d)}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          ) : (
            <p className="add-item-notice-p">Nog geen uitzonderingen toegevoegd.</p>
          )}
        </div>
      )}
    </div>
  );
};
